// Copyright 2026 Hardfin, Inc.
import { AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import { assetSearchListingSchema } from '@/asset/hooks/use-asset-filters';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { AssetTableColumn } from '@/constants/asset-table-column';
import { useSort } from '@/hooks/use-sort';
import { LocationErrorBoundary } from '@/location/components/location-error-boundary';
import { useLocation } from '@/location/hooks/use-location';
import { Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

const ASSET_TABLE_COLUMNS: AssetTableColumn[] = [
  'serial',
  'product',
  'zone',
  'owner',
  'activity',
];

export const Route = createFileRoute(
  '/_app/locations/$locationKey/related-assets'
)({
  component: RelatedAssetsRoute,
  validateSearch: assetSearchListingSchema,
});

function RelatedAssetsRoute() {
  const { locationKey } = Route.useParams();
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleChangePage = (page: number) => {
    navigate({ search: { ...searchParameters, page } });
  };

  return (
    <LocationErrorBoundary>
      <RelatedAssetsContent
        locationKey={locationKey}
        page={searchParameters.page}
        handlePageChange={handleChangePage}
      />
    </LocationErrorBoundary>
  );
}

interface RelatedAssetsContentProps {
  locationKey: string;
  page?: number;
  handlePageChange?: (page: number) => void;
}

function RelatedAssetsContent(props: RelatedAssetsContentProps) {
  const { location } = useLocation(props.locationKey);
  const { sort, setSort } = useSort<AssetSortProperty>('serial');

  return (
    <HardfinTabCardWrapper title="Related assets">
      <Stack paddingTop={2}>
        <Suspense
          fallback={<AssetTableSkeleton columns={ASSET_TABLE_COLUMNS.length} />}
        >
          <AssetTable
            sort={sort}
            columns={ASSET_TABLE_COLUMNS}
            filter={{
              locationIds: [location.id, ...location.zones.map((z) => z.id)],
              pageSize: 50,
              pageNumber: props.page ?? 1,
            }}
            onSort={setSort}
            onPageChange={props.handlePageChange}
            emptyState={
              <Typography variant="body2" color="text.secondary">
                There are no assets at this location
              </Typography>
            }
          />
        </Suspense>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
